import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Save } from 'lucide-react'
import { advertiserApi, Advertiser } from '../../lib/api/advertisers'
import { campaignApi } from '../../lib/api/campaigns'
import { reportApi } from '../../lib/api/reports'
import { savedReportApi } from '../../lib/api/savedReports'
import { ReportBreakdown, ReportDataRow, ReportDateRange } from '../../types/reports'
import { MANDATORY_METRICS, isDerivedMetric } from './metricsConfig'

const DEFAULT_SELECTED_METRICS = [...MANDATORY_METRICS, 'ctr']
import { ReportFiltersBar } from './components/ReportFiltersBar'
import { ReportSummaryCards } from './components/ReportSummaryCards'
import { ReportChart } from './components/ReportChart'
import { ReportTable } from './components/ReportTable'
import { SaveReportModal } from './components/SaveReportModal'
import { useRole } from '../../hooks/useRole'

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function defaultDateFrom(): string {
  const d = new Date()
  d.setDate(d.getDate() - 30)
  return toIsoDate(d)
}

function defaultDateTo(): string {
  return toIsoDate(new Date())
}

export function ReportsPage() {
  const { user, isAdmin } = useRole()
  const { campaignId, adUnitId, uid } = useParams<{
    campaignId?: string
    adUnitId?: string
    uid?: string
  }>()
  const navigate = useNavigate()
  const urlInitialized = useRef(false)
  const advertiserFetchId = useRef<number | null>(null)

  // Filter state
  const [selectedAdvertiser, setSelectedAdvertiser] = useState<Advertiser | null>(null)
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<number[]>([])
  const [selectedAdUnitIds, setSelectedAdUnitIds] = useState<number[]>([])
  const [breakdowns, setBreakdowns] = useState<ReportBreakdown[]>(['campaigns'])
  const [dateFrom, setDateFrom] = useState(defaultDateFrom)
  const [dateTo, setDateTo] = useState(defaultDateTo)
  const [timezone, setTimezone] = useState('')
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(DEFAULT_SELECTED_METRICS)

  // Report result state
  const [reportData, setReportData] = useState<ReportDataRow[]>([])
  const [dateRange, setDateRange] = useState<ReportDateRange | null>(null)
  const [queryTimeMs, setQueryTimeMs] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [hasRun, setHasRun] = useState(false)
  const [isReportQueued, setIsReportQueued] = useState(false)
  const [isChartExpanded, setIsChartExpanded] = useState(false)

  // Saved report state
  const [savedReportId, setSavedReportId] = useState<number | null>(null)
  const [savedReportName, setSavedReportName] = useState<string | null>(null)
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false)
  const [isSaveAsMode, setIsSaveAsMode] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Auto-select advertiser for non-admin users (only when no URL params)
  useEffect(() => {
    if (!campaignId && !uid && !isAdmin() && user?.manageable_id && !selectedAdvertiser) {
      advertiserApi.getAdvertiser(Number(user.manageable_id))
        .then((a) => {
          setSelectedAdvertiser(a)
          setTimezone(a.time_zone ?? '')
        })
        .catch(() => {})
    }
  }, [user, isAdmin, selectedAdvertiser, campaignId, uid])

  // Load saved report when uid param is present
  useEffect(() => {
    if (!uid || urlInitialized.current) return
    urlInitialized.current = true

    const parsedId = Number(uid)
    if (isNaN(parsedId)) return

    savedReportApi.get(parsedId)
      .then(async (saved) => {
        const advertiser = await advertiserApi.getAdvertiser(saved.advertiser_id).catch(() => ({
          id: saved.advertiser_id,
          name: saved.advertiser?.name ?? '',
        }))

        const sp = saved.saved_params
        const loadedAdvertiser: Advertiser = { ...advertiser, id: saved.advertiser_id }
        const campaignIds = sp.campaign_id ?? []
        const adUnitIds = sp.ad_unit_id ?? []
        const loadedBreakdowns: ReportBreakdown[] = sp.breakdowns ?? ['campaigns']
        const loadedDateFrom = sp.date_from ?? defaultDateFrom()
        const loadedDateTo = sp.date_to ?? defaultDateTo()
        const loadedTimezone = sp.timezone ?? loadedAdvertiser.time_zone ?? ''
        const loadedMetrics = sp.selected_metrics ?? DEFAULT_SELECTED_METRICS

        setSelectedAdvertiser(loadedAdvertiser)
        setSelectedCampaignIds(campaignIds)
        setSelectedAdUnitIds(adUnitIds)
        setBreakdowns(loadedBreakdowns)
        setDateFrom(loadedDateFrom)
        setDateTo(loadedDateTo)
        setTimezone(loadedTimezone)
        setSelectedMetrics(loadedMetrics)
        setSavedReportId(saved.id)
        setSavedReportName(saved.name)

        await runReport(loadedAdvertiser, campaignIds, adUnitIds, loadedBreakdowns, loadedDateFrom, loadedDateTo, loadedTimezone, loadedMetrics)
      })
      .catch(() => toast.error('Failed to load saved report'))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid])

  // Initialize from URL params: /reports/campaigns/:campaignId/ad_units/:adUnitId
  useEffect(() => {
    if (!campaignId || urlInitialized.current) return
    urlInitialized.current = true

    const parsedCampaignId = Number(campaignId)
    const parsedAdUnitId = adUnitId ? Number(adUnitId) : null

    campaignApi.getCampaign(parsedCampaignId)
      .then(async (campaign) => {
        const advertiser: Advertiser = campaign.advertiser ?? { id: campaign.advertiser_id, name: '' }
        if (!advertiser.name || !advertiser.time_zone) {
          try {
            const full = await advertiserApi.getAdvertiser(campaign.advertiser_id)
            advertiser.name = full.name
            advertiser.time_zone = full.time_zone
          } catch {}
        }

        const campaignIds = [parsedCampaignId]
        const adUnitIds = parsedAdUnitId ? [parsedAdUnitId] : []
        const activeBreakdowns: ReportBreakdown[] = adUnitIds.length ? ['ad_units'] : ['campaigns']
        const tz = advertiser.time_zone ?? ''

        setSelectedAdvertiser(advertiser)
        setSelectedCampaignIds(campaignIds)
        setSelectedAdUnitIds(adUnitIds)
        setBreakdowns(activeBreakdowns)
        setTimezone(tz)

        await runReport(advertiser, campaignIds, adUnitIds, activeBreakdowns, dateFrom, dateTo, tz)
      })
      .catch(() => toast.error('Failed to load campaign for report'))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, adUnitId])

  async function runReport(
    advertiser: Advertiser,
    campaignIds: number[],
    adUnitIds: number[],
    activeBreakdowns: ReportBreakdown[],
    from: string,
    to: string,
    tz: string,
    activeSelectedMetrics: string[] = selectedMetrics,
  ) {
    setIsLoading(true)
    setHasRun(false)
    setIsReportQueued(false)

    // Only pass non-mandatory, non-derived metric keys to the backend
    const backendMetrics = activeSelectedMetrics.filter(
      (m) => !(MANDATORY_METRICS as readonly string[]).includes(m) && !isDerivedMetric(m)
    )

    try {
      const result = await reportApi.getReport({
        advertiser_id: advertiser.id,
        campaign_id: campaignIds.length ? campaignIds : undefined,
        ad_unit_id: adUnitIds.length ? adUnitIds : undefined,
        breakdowns: activeBreakdowns,
        date_from: from,
        date_to: to,
        timezone: tz || undefined,
        metrics: backendMetrics.length ? backendMetrics : undefined,
      })

      if (result.queued) {
        setIsReportQueued(true)
        if (result.download_url) {
          const a = document.createElement('a')
          a.href = result.download_url
          a.download = ''
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
        }
        return
      }

      const data = result.report?.data ?? []
      setReportData(data)
      setDateRange(result.report?.date_range ?? null)
      setQueryTimeMs(result.report?.query_time_ms ?? 0)
      setHasRun(true)

      if (data.length === 0) {
        toast('No data found for the selected filters', { icon: 'ℹ️' })
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to fetch report')
    } finally {
      setIsLoading(false)
    }
  }

  async function handleRunReport() {
    if (!selectedAdvertiser) {
      toast.error('Please select an advertiser first')
      return
    }
    await runReport(selectedAdvertiser, selectedCampaignIds, selectedAdUnitIds, breakdowns, dateFrom, dateTo, timezone)
  }

  async function handleAdvertiserChange(a: Advertiser | null) {
    setSelectedAdvertiser(a)
    setSelectedCampaignIds([])
    setSelectedAdUnitIds([])
    setHasRun(false)
    setIsReportQueued(false)
    setReportData([])
    setSavedReportId(null)
    setSavedReportName(null)

    if (!a) {
      advertiserFetchId.current = null
      setTimezone('')
      return
    }

    if (a.time_zone) {
      advertiserFetchId.current = null
      setTimezone(a.time_zone)
      return
    }

    advertiserFetchId.current = a.id
    try {
      const full = await advertiserApi.getAdvertiser(a.id)
      if (advertiserFetchId.current !== a.id) return
      setSelectedAdvertiser(full)
      setTimezone(full.time_zone ?? '')
    } catch {
      if (advertiserFetchId.current === a.id) setTimezone('')
    }
  }

  function buildSavedParams() {
    return {
      campaign_id: selectedCampaignIds.length ? selectedCampaignIds : undefined,
      ad_unit_id: selectedAdUnitIds.length ? selectedAdUnitIds : undefined,
      breakdowns,
      date_from: dateFrom,
      date_to: dateTo,
      timezone: timezone || undefined,
      selected_metrics: selectedMetrics,
    }
  }

  async function handleSave() {
    if (!selectedAdvertiser) {
      toast.error('Please select an advertiser first')
      return
    }
    if (savedReportId) {
      // Update existing saved report silently
      setIsSaving(true)
      try {
        await savedReportApi.update(savedReportId, { saved_params: buildSavedParams() })
        toast.success('Report saved')
      } catch {
        toast.error('Failed to save report')
      } finally {
        setIsSaving(false)
      }
    } else {
      setIsSaveAsMode(false)
      setIsSaveModalOpen(true)
    }
  }

  async function handleSaveAs() {
    if (!selectedAdvertiser) {
      toast.error('Please select an advertiser first')
      return
    }
    setIsSaveAsMode(true)
    setIsSaveModalOpen(true)
  }

  async function handleSaveConfirm(name: string) {
    if (!selectedAdvertiser) return
    setIsSaving(true)
    try {
      const created = await savedReportApi.create({
        name,
        advertiser_id: selectedAdvertiser.id,
        saved_params: buildSavedParams(),
      })
      setSavedReportId(created.id)
      setSavedReportName(created.name)
      setIsSaveModalOpen(false)
      toast.success('Report saved')
      navigate(`/reports/${created.id}`, { replace: true })
    } catch {
      toast.error('Failed to save report')
    } finally {
      setIsSaving(false)
    }
  }

  const canSave = !!selectedAdvertiser && hasRun

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-950 px-6 py-6">
      <div className="max-w-screen-xl mx-auto">
        {/* Page heading */}
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {savedReportName ?? 'Reports'}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {savedReportName
                ? 'Saved report — edit filters and re-run anytime'
                : 'Analyze campaign performance across dimensions'}
            </p>
          </div>

          {canSave && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {savedReportId ? 'Save' : 'Save'}
              </button>
              <button
                onClick={handleSaveAs}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
              >
                Save as
              </button>
            </div>
          )}
        </div>

        {/* Filters */}
        <ReportFiltersBar
          selectedAdvertiser={selectedAdvertiser}
          onAdvertiserChange={handleAdvertiserChange}
          selectedCampaignIds={selectedCampaignIds}
          onCampaignIdsChange={(ids) => { setSelectedCampaignIds(ids); setSelectedAdUnitIds([]) }}
          selectedAdUnitIds={selectedAdUnitIds}
          onAdUnitIdsChange={setSelectedAdUnitIds}
          breakdowns={breakdowns}
          onBreakdownsChange={setBreakdowns}
          selectedMetrics={selectedMetrics}
          onMetricsChange={setSelectedMetrics}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          timezone={timezone}
          onTimezoneChange={setTimezone}
          onRunReport={handleRunReport}
          isLoading={isLoading}
        />

        {/* Queued report notice */}
        {isReportQueued && !isLoading && (
          <div className="mt-6 flex items-start gap-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl px-6 py-5">
            <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-800/40 flex items-center justify-center">
              <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Report is being prepared</p>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-0.5">
                This report is too large to display here. We are preparing your report — when it is ready, the report will be sent to your registered email.
              </p>
            </div>
          </div>
        )}

        {/* Results */}
        {(hasRun || isLoading) && (
          <>
            {hasRun && (
              <ReportSummaryCards
                data={reportData}
                dateRange={dateRange}
                queryTimeMs={queryTimeMs}
                selectedMetrics={selectedMetrics}
              />
            )}
            {hasRun && reportData.length > 0 && (
              <div className="mt-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                <button
                  onClick={() => setIsChartExpanded(v => !v)}
                  className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <span>Chart</span>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isChartExpanded ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isChartExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-800">
                    <ReportChart data={reportData} breakdowns={breakdowns} selectedMetrics={selectedMetrics} />
                  </div>
                )}
              </div>
            )}
            <ReportTable
              data={reportData}
              breakdowns={breakdowns}
              isLoading={isLoading}
              timezone={timezone}
              selectedMetrics={selectedMetrics}
            />
          </>
        )}

        {/* Initial state prompt */}
        {!hasRun && !isLoading && !isReportQueued && (
          <div className="mt-8 text-center py-16 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl">
            <div className="w-14 h-14 rounded-full bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <p className="text-gray-500 dark:text-gray-400 font-medium">
              Select an advertiser and click <strong className="text-gray-700 dark:text-gray-200">Run Report</strong> to see data
            </p>
          </div>
        )}
      </div>

      <SaveReportModal
        isOpen={isSaveModalOpen}
        initialName={isSaveAsMode ? (savedReportName ?? '') : ''}
        onConfirm={handleSaveConfirm}
        onClose={() => setIsSaveModalOpen(false)}
        isSaving={isSaving}
      />
    </div>
  )
}
